import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

import {
  Language,
  SUPPORTED_LANGUAGES,
} from '../types';

interface LanguageContextType {
  language: Language | null;
  hasSelectedLanguage: boolean;
  setLanguage: (lang: Language) => void;
  clearLanguage: () => void;
}

const LanguageContext = createContext<
  LanguageContextType | undefined
>(undefined);

const STORAGE_KEY = 'gallery-g-language';

export function LanguageProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] =
    useState<Language | null>(null);

  const [isInitialized, setIsInitialized] =
    useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (
      stored &&
      SUPPORTED_LANGUAGES.includes(
        stored as Language
      )
    ) {
      setLanguageState(stored as Language);
    }

    setIsInitialized(true);
  }, []);

  const setLanguage = (lang: Language) => {
    localStorage.setItem(STORAGE_KEY, lang);

    setLanguageState(lang);
  };

  const clearLanguage = () => {
    localStorage.removeItem(STORAGE_KEY);

    setLanguageState(null);
  };

  const value = useMemo(
    () => ({
      language,
      hasSelectedLanguage: !!language,
      setLanguage,
      clearLanguage,
    }),
    [language]
  );

  // Prevent flicker before localStorage loads
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      'useLanguage must be used within LanguageProvider'
    );
  }

  return context;
}